<script setup lang="ts">
import useAsync from "~/hooks/use-async";
import useFollow from "~/hooks/use-follow";
import useSSOLogin from "~/hooks/use-sso-login";
import CategoriesAPI from "~/services/categories";
import useUserStore from "~/stores/user";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const { executeSocialLogin, socialLoginLoading } = useSSOLogin();
const appConfig = useRuntimeConfig().public;

const isGameHighlightsEnabled = appConfig.APP_CUSTOMER.enable_game_highlights;
const isLoggedIn = computed(() => Boolean(userStore.user));
const subCategoryId = computed(() => route.params.subId as string);
const mainCategoryId = computed(() => route.params.mainId as string);
const { loading: isFollowingDisabled, isFollowing, toggleFollow } = useFollow({ id: subCategoryId.value, type: "identity_item" });
const { data: identityItem, loading, error, run: loadSubCategory } = useAsync(CategoriesAPI.subCategories.loadById);
const noContent = computed(() => identityItem.value?.totalEventsNumber === 0);

async function onClickFollow(item: IModelIdentityItem) {
  if (!isLoggedIn.value) {
    if (appConfig.APP_FLAGS.hasIfaAuth && appConfig.APP_CUSTOMER.app_config.enable_skip_login_page_for_ifa_sso)
      return executeSocialLogin("ifa");
    return router.push({ path: "/auth/login/", query: { redirectUrl: route.fullPath } });
  }

  return toggleFollow(item);
}

watch(
  () => route.params.subId,
  subCategoryId => subCategoryId && loadSubCategory(subCategoryId as string, { mainCategoryId: route.params.mainId, loadTotals: true }),
  { immediate: true },
);

watch(error, () => showError({ statusCode: 404, data: { context: "page" } }));

definePageMeta({ layout: "home" });
</script>

<template>
  <div>
    <div>
      <BlockIdentitySkeleton v-if="loading" has-following-button />

      <BlockIdentity
        v-else-if="identityItem"
        has-following-button
        has-events-number
        :live-events-number="identityItem.totalLiveEventsNumber"
        :upcoming-events-number="identityItem.totalUpcomingEventsNumber"
        :total-events-number="identityItem.totalEventsNumber"
        :facebook-url="identityItem.facebookUrl"
        :x-url="identityItem.xUrl"
        :instagram-url="identityItem.instagramUrl"
        :web-url="identityItem.webUrl"
        :logo-url="identityItem.logoUrl"
        :banners="identityItem.banners"
        :title="identityItem.title"
        :is-following="isFollowing"
        :is-following-disabled="isFollowingDisabled || socialLoginLoading"
        @follow="onClickFollow(identityItem as IModelIdentityItem)"
      />
    </div>
    <div class="text-2xl main-container text-main">
      <ListEvents
        :title="t('labels.live')"
        :sub-category-id="subCategoryId"
        :main-category-id="mainCategoryId"
        type="live"
        hide-on-no-content
        has-see-all-link
        see-all-link-placement="top-right"
      >
        <template #end>
          <div class="my-12 hr" role="separator" />
        </template>
      </ListEvents>

      <ListEvents
        :title="t('labels.upcoming')"
        :sub-category-id="subCategoryId"
        :main-category-id="mainCategoryId"
        type="upcoming"
        hide-on-no-content
        has-see-all-link
        see-all-link-placement="top-right"
      >
        <template #end>
          <div class="my-12 hr" role="separator" />
        </template>
      </ListEvents>

      <ListEvents
        v-if="isGameHighlightsEnabled"
        :title="t('labels.latest_highlights')"
        :sub-category-id="subCategoryId"
        :main-category-id="mainCategoryId"
        type="highlight"
        hide-on-no-content
        has-see-all-link
      >
        <template #end>
          <div class="my-12 hr" role="separator" />
        </template>
      </ListEvents>

      <ListEvents
        :title="t('labels.latest')"
        :sub-category-id="subCategoryId"
        :main-category-id="mainCategoryId"
        hide-on-no-content
        has-see-all-link
      />
    </div>
  </div>

  <BaseNotFound v-if="noContent && !loading" :text="t('labels.empty_events_list')">
    <template #icon="{ isDarkTheme }">
      <IconEventNotfound :is-dark-theme="isDarkTheme" />
    </template>
  </BaseNotFound>
</template>
